import { useEffect, useState, type ReactNode } from 'react';
import { admin } from '@/lib/api';
import NotFound from '@/pages/NotFound';

/**
 * Wraps an admin-only page. The wrapper renders NOTHING during the
 * brief auth probe, then either the wrapped children (for admins) or
 * the standalone <NotFound /> page (for everyone else).
 *
 * The point is that the admin URL should look identical to any other
 * 404 from a non-admin's perspective — no DashboardLayout chrome, no
 * page title, no sub-nav, no "Admin access required" message. The
 * previous behavior was to render the admin page chrome with an
 * inline error: that leaked "this URL exists and has these sub-tools"
 * to anyone probing.
 *
 * Auth source: /api/admin/status returns 200 + {isAdmin: bool} for
 * authenticated users, 401 for unauth. Either non-true or any error
 * → render NotFound. Returns null during the probe so non-admins see
 * a brief blank → NotFound (indistinguishable from a slow 404),
 * while admins see a brief blank → real page.
 *
 * Defense in depth: the SERVER (every admin endpoint) still checks
 * admin status independently via requireAdmin. This guard is purely
 * UI obfuscation — the data is server-protected regardless.
 */
export function AdminGuard({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'loading' | 'allowed' | 'denied'>('loading');

  useEffect(() => {
    admin
      .status()
      .then((r) => setState(r.isAdmin ? 'allowed' : 'denied'))
      .catch(() => setState('denied'));
  }, []);

  if (state === 'loading') return null;
  if (state === 'denied') return <NotFound />;
  return <>{children}</>;
}
